import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Zap, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import useReportData from '@/hooks/useReportData';
import ReportHeader from '@/components/report/ReportHeader';
import ReportPreview from '@/components/report/ReportPreview';
import PricingCards from '@/components/report/PricingCards';
import FullReport from '@/components/report/FullReport';
import AIChatSidebar from '@/components/report/AIChatSidebar';
import { translateHomeType, translateCountry } from '@/utils/translations';
import { trackFunnelEvent } from '@/utils/tracking';

const API = '/api';

export default function ReportPage() {
  const { reportId } = useParams();
  const { i18n, t } = useTranslation();
  const { report, loading, polling, error, reload } = useReportData(reportId);
  const [sessionId] = useState(`session-${Date.now()}`);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (report && !loading && !polling) {
      trackFunnelEvent(report.unlocked ? 'report_viewed' : 'preview_viewed', { report_id: reportId, country: report.country });
      if (!report.unlocked && window.pwTrack) window.pwTrack('preview_view', { report_id: reportId });
      // Set UI language to match the report's stored language
      if (report.language && report.language !== i18n.language) {
        i18n.changeLanguage(report.language);
      }
    }
  }, [report, loading, polling, reportId, i18n]);

  const handleSendEmail = async () => {
    if (!report?.unlocked) {
      toast.error(t('report.unlockFirst'));
      return;
    }
    const email = report?.quiz_data?.step5?.email;
    if (!email) {
      toast.error(t('report.noEmail'));
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-report`, { email, report_id: reportId, language: i18n.language });
      toast.success(t('report.emailSent'));
    } catch {
      toast.error(t('report.emailError'));
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading || polling) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          {polling ? <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" /> : <Zap className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />}
          <p className="text-muted-foreground">{polling ? t('payment.verifying') : t('report.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-foreground mb-2">{t('report.loadError', 'Rapport niet gevonden')}</h2>
          <p className="text-muted-foreground mb-6">{t('report.loadErrorDesc', 'Het rapport kan tijdelijk niet worden geladen. Probeer het opnieuw.')}</p>
          <button onClick={reload} className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium" data-testid="report-retry-btn">
            {t('report.retry', 'Opnieuw proberen')}
          </button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const isUnlocked = report.unlocked;

  return (
    <div className="min-h-screen bg-background" data-testid="report-page">
      <ReportHeader onSendEmail={handleSendEmail} sendingEmail={sendingEmail} isUnlocked={isUnlocked} />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8" data-testid="report-header">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-primary mb-4" style={{ fontFamily: 'Outfit' }}>
              {isUnlocked ? t('report.fullTitle') : t('report.previewPageTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('report.forHome', { type: translateHomeType(report.home_profile.home_type, t), country: translateCountry(report.country || report.home_profile?.country, i18n.language) })}
            </p>
          </div>

          {isUnlocked ? (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <FullReport report={report} />
              </div>
              <div className="lg:col-span-1">
                <AIChatSidebar reportId={reportId} sessionId={sessionId} />
              </div>
            </div>
          ) : (
            <>
              <ReportPreview report={report} />
              <PricingCards reportId={reportId} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
